import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Toast from '../components/Toast';
import { jsonDeleteResponse, jsonPost, jsonPut } from '../api/jsonRequest';
import { getAuthConfig } from '../mixins/ApiConfig';
import { queryKeys } from '../query/queryKeys';
import { getUploadErrorMessage } from '../utils/uploadLimits';
import { usePollQuery } from '../query/usePollQuery';

function uploadErrorToast(error, fallbackTitle) {
    const sizeMessage = getUploadErrorMessage(error);
    Toast.fire({
        title: sizeMessage || fallbackTitle,
        icon: 'error',
    });
}

export function useProcessorVersion({ enabled = true, pauseRefetch = false } = {}) {
    return usePollQuery({
        queryKey: queryKeys.account.processorVersion(),
        url: '/api/Account/GetProcessorVersion',
        enabled,
        pauseRefetch,
    });
}

export function useAppUrls({ enabled = true, pauseRefetch = false } = {}) {
    return usePollQuery({
        queryKey: queryKeys.apps.urls(),
        url: '/api/AppVersionFile/FetchAppUrls',
        enabled,
        pauseRefetch,
    });
}

export function useTutorialVideos({ enabled = true, pauseRefetch = false } = {}) {
    return usePollQuery({
        queryKey: queryKeys.tutorials.videos(),
        url: '/api/PDFFile/FetchVideoUrl',
        enabled,
        pauseRefetch,
    });
}

export function useTutorialCategories({ enabled = true, pauseRefetch = false } = {}) {
    return usePollQuery({
        queryKey: queryKeys.tutorials.categories(),
        url: '/api/PDFFile/FetchVideoCat',
        enabled,
        pauseRefetch,
    });
}

export function useUploadAppFile() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ file, companyId, name }) => {
            const config = getAuthConfig();
            const fileUploadConfig = { ...config };
            delete fileUploadConfig.headers['Content-Type'];
            const formData = new FormData();
            formData.append('file', file);
            formData.append('companyId', companyId);
            formData.append('name', name);
            const res = await axios.post(
                '/api/AppVersionFile/AppVersionFileUpload',
                formData,
                fileUploadConfig,
            );
            if (res.status !== 200) {
                throw new Error('Upload failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'App Uploaded', icon: 'success' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.apps.all() });
        },
        onError: (error) => {
            uploadErrorToast(error, 'Error in upload');
        },
    });
}

export function useDeleteApp() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (appId) => {
            const res = await jsonDeleteResponse(`/api/AppVersionFile/DeleteAppUrl/${appId}`);
            if (res.status !== 200) {
                throw new Error('Delete failed');
            }
            return res.data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.apps.all() });
        },
    });
}

export function useUploadVideoUrl() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPost('/api/PDFFile/UploadVideoUrl', payload);
            if (res.status != 200) {
                throw new Error('Error with saving video Url');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Video Url Saved.', icon: 'success' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tutorials.all() });
        },
        onError: () => {
            Toast.fire({ title: 'Error with saving video Url.', icon: 'error' });
        },
    });
}

export function useDeleteVideo() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (videoId) => {
            const res = await jsonDeleteResponse(`/api/PDFFile/DeleteVideoUrl/${videoId}`);
            if (res.status !== 200) {
                throw new Error('Delete failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Video Deleted', icon: 'warning' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tutorials.all() });
        },
    });
}

export function useSaveVideoCat() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPost('/api/PDFFile/SaveVideoCat', payload);
            if (res.status !== 200) {
                throw new Error('Save failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Video Category Saved', icon: 'success' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tutorials.all() });
        },
    });
}

export function useUpdateVideoCategory() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPut('/api/PDFFile/UpdateVideoCat', payload);
            if (res.status !== 200) {
                throw new Error('Update failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Video Category Updated', icon: 'success' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tutorials.all() });
        },
    });
}

export function useDeleteVideoCategory() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (categoryId) => {
            const res = await jsonDeleteResponse(
                `/api/PDFFile/DeleteVideoCategory/${categoryId}`,
            );
            if (res.status !== 200) {
                throw new Error('Delete failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Video Category Deleted', icon: 'warning' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tutorials.all() });
        },
    });
}

export function useUpdateVideo() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPut('/api/PDFFile/UpdateVideo', payload);
            if (res.status !== 200) {
                throw new Error('Update failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'Video Updated', icon: 'success' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tutorials.all() });
        },
    });
}

export function useDownloadApp() {
    return useMutation({
        mutationFn: async ({ id, appVersion }) => {
            const config = getAuthConfig();
            const ZIPfile = await axios.get(
                `/api/AppVersionFile/AppDownload/${id}`,
                { ...config, responseType: 'arraybuffer' },
            );
            if (ZIPfile.status !== 200) {
                throw new Error('App Version Not Found');
            }
            const zipfFile = new Blob([ZIPfile.data], { type: 'application/zip' });
            const ZIPURL = URL.createObjectURL(zipfFile);
            const a = document.createElement('a');
            a.href = ZIPURL;
            a.download = `${appVersion}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(ZIPURL);
        },
        onSuccess: () => {
            Toast.fire({ title: 'App Version Found', icon: 'success' });
        },
        onError: (error) => {
            console.log('Fetch App file error:' + error);
            Toast.fire({ title: 'App Version Not Found', icon: 'error' });
        },
    });
}
